import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, Refresh, CheckCircle, Warning, Error, Schedule } from '@mui/icons-material';
import { apiHealthService, APIHealth } from '../services/apiHealthService';
import Layout from '../components/Layout';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280, // 80px for sidebar + 200px for submenu
}));

const APIMonitoringPage: React.FC = () => {
  const [healthData, setHealthData] = useState<Map<string, APIHealth[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadHealthData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiHealthService.checkAllHealth();
      setHealthData(data);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      let errorMessage = 'Failed to load health data';
      if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealthData();
    
    // Set up listener for real-time updates
    const unsubscribe = apiHealthService.addListener((environment, data) => {
      setHealthData(prev => new Map(prev).set(environment, data));
    });

    return unsubscribe;
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      case 'loading':
        return <Schedule color="action" />;
      default:
        return <Schedule color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'loading':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatResponseTime = (time?: number) => {
    if (!time) return 'N/A';
    return `${time.toFixed(0)}ms`;
  };

  const formatLastChecked = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <PageContainer>
      <Layout>
        <MainContent>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1">
                System Health Monitoring
              </Typography>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={loadHealthData}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Information about health checks */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Health Check Information:</strong> This page monitors the health of all PropTrak and Aletheia services. 
                Frontend checks verify successful page loads (200 status), while API checks verify endpoint availability. 
                <br /><br />
                <strong>Error Types:</strong>
                <br />• <strong>CORS/Network blocked:</strong> Service is accessible but blocked by browser security - requires Application Gateway routing in production
                <br />• <strong>404/503:</strong> Service endpoint not found or unavailable
                <br />• <strong>Other errors:</strong> Network issues or service problems
              </Typography>
            </Alert>

            {/* Last Updated */}
            {lastUpdated && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Last updated: {lastUpdated.toLocaleString()}
                </Typography>
              </Alert>
            )}

            {/* Environment Tabs */}
            {Array.from(healthData.keys()).map((environment) => {
              const apis = healthData.get(environment) || [];
              const healthyCount = apis.filter(api => api.status === 'healthy').length;
              const totalCount = apis.length;
              
              return (
                <Accordion key={environment} defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="h6" sx={{ flexGrow: 1, textTransform: 'capitalize' }}>
                        {environment} Environment
                      </Typography>
                      <Chip
                        label={`${healthyCount}/${totalCount} Healthy`}
                        color={healthyCount === totalCount ? 'success' : healthyCount > 0 ? 'warning' : 'error'}
                        size="small"
                        sx={{ mr: 2 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                      {apis.map((api, index) => (
                        <Card key={index}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              {getStatusIcon(api.status)}
                              <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                                {api.name}
                              </Typography>
                              <Chip
                                label={api.status}
                                color={getStatusColor(api.status) as any}
                                size="small"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {api.url}
                            </Typography>
                            
                            {api.error && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {api.error}
                              </Alert>
                            )}
                            
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell component="th" scope="row">Response Time</TableCell>
                                    <TableCell>{formatResponseTime(api.responseTime)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell component="th" scope="row">Last Checked</TableCell>
                                    <TableCell>{formatLastChecked(api.lastChecked)}</TableCell>
                                  </TableRow>
                                  {api.details && Object.keys(api.details).length > 0 && (
                                    <TableRow>
                                      <TableCell component="th" scope="row">Details</TableCell>
                                      <TableCell>
                                        <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                          {JSON.stringify(api.details, null, 2)}
                                        </pre>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {healthData.size === 0 && !loading && (
              <Alert severity="info">
                No health data available. Click refresh to start monitoring.
              </Alert>
            )}
          </Box>
        </MainContent>
      </Layout>
    </PageContainer>
  );
};

export default APIMonitoringPage; 